import {
    Alert,
    Button,
    Card,
    FormButtonRow,
    FormGroup,
    Heading,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { useState } from 'react';
import type { IBankAccountImportConflict } from '@common/interfaces/';
import { ManagerAuthorization, ValidationErrorCode } from '@common/enums/';
import { formatString } from '@common/utils/formatString';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import { SuccessFlashWhenSaved } from '../../elements/common';
import { FileInput } from '../../elements/FileInput';

interface IFile {
    data: string | null;
    fileName: string | null;
}

export function ImportBankAccountDetails() {
    const [file, setFile] = useState<IFile | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [successSavedCounter, setSuccessSavedCounter] = useState<number>(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [requestedBankAccountConflicts, setRequestedBankAccountConflicts] = useState<IBankAccountImportConflict[]>(
        []
    );
    const [notRequestedBankAccountConflicts, setNotRequestedBankAccountConflicts] = useState<
        IBankAccountImportConflict[]
    >([]);
    const [successMessage, setSuccessMessage] = useState<string | undefined>();
    const [infoMessage, setInfoMessage] = useState<string | undefined>();

    const translateKey = getSelectedLanguage();

    assertHasAuthorization(ManagerAuthorization.importBankAccounts);

    return (
        <PageTemplate width="medium">
            <PageHeader title={translateKey.imports.import} />

            {errorMessage && <Alert appearance="warning">{errorMessage}</Alert>}

            {infoMessage ? (
                renderInfoMessage()
            ) : (
                <SuccessFlashWhenSaved
                    message={successMessage}
                    savedCounter={successSavedCounter}
                />
            )}

            <Card>
                <Card.Header
                    title={translateKey.imports.bankAccountDetails}
                    tooltipContent={translateKey.imports.bankAccountDetailsTooltipText}
                />
                <Card.Body>
                    <FormGroup label={translateKey.common.file}>
                        <FileInput
                            files={file ? [file] : null}
                            onChange={async (result) => setFile(result)}
                            onError={async (message: string) => setErrorMessage(message)}
                        />
                    </FormGroup>
                    <FormButtonRow>
                        <Button
                            isDisabled={isLoading}
                            onClick={async () => await uploadFile()}
                        >
                            {translateKey.upload.upload}
                        </Button>
                    </FormButtonRow>
                    {requestedBankAccountConflicts.length !== 0 && (
                        <>
                            <Alert
                                appearance="warning"
                                hasIcon={true}
                            >
                                {translateKey.imports.bankAccountConflicts}
                            </Alert>
                            <Heading
                                mb={4}
                                size="large"
                            >
                                {translateKey.imports.requestedBankAccountConflicts}
                            </Heading>
                            {renderImportConflicts(requestedBankAccountConflicts)}
                        </>
                    )}
                    {notRequestedBankAccountConflicts.length !== 0 && (
                        <>
                            <Heading
                                mb={4}
                                size="large"
                            >
                                {translateKey.imports.notRequestedBankAccountConflicts}
                            </Heading>
                            {renderImportConflicts(notRequestedBankAccountConflicts)}
                        </>
                    )}
                </Card.Body>
            </Card>
        </PageTemplate>
    );

    async function uploadFile() {
        if (file) {
            const result = await fetchJson('/api/bankAccountDetails/import', 'POST', { file: file }, [
                ValidationErrorCode.IMPORT_WRONG_FILE_TYPE,
            ]);

            if (result.errorCode) {
                const fileName = result.message || '';
                if (result.errorCode === ValidationErrorCode.IMPORT_WRONG_FILE_TYPE) {
                    setIsLoading(false);
                    setErrorMessage(formatString(translateKey.upload.wrongFileFormat, [fileName]));
                }
            } else {
                setRequestedBankAccountConflicts(result.json.requestedBankAccountConflicts);
                setNotRequestedBankAccountConflicts(result.json.notRequestedBankAccountConflicts);
                setSuccessSavedCounter(successSavedCounter + 1);
                setIsLoading(false);
                if (result.json.totalRowsImported === 0) {
                    setInfoMessage(translateKey.imports.noBankAccountsUpdated);
                    setSuccessMessage(undefined);
                } else {
                    setInfoMessage(undefined);
                    setSuccessMessage(
                        result.json.totalRowsImported === 1
                            ? translateKey.imports.oneBankAccountImported
                            : formatString(translateKey.imports.severalBankAccountsImported, [
                                  result.json.totalRowsImported,
                              ])
                    );
                }
                setErrorMessage(null);
            }
        }
    }

    function renderImportConflicts(conflicts: IBankAccountImportConflict[]) {
        if (conflicts.length === 0) {
            return;
        }

        return (
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey.employment.relationNumber}</TableHeader>
                            <TableHeader>{translateKey.common.name}</TableHeader>
                            <TableHeader>{translateKey.organization.organization}</TableHeader>
                            <TableHeader>In ClientSafe</TableHeader>
                            <TableHeader>In import Exact</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {conflicts.map((conflict) => (
                            <TableRow key={conflict.relationNumber}>
                                <TableCell>{conflict.relationNumber}</TableCell>
                                <TableCell>{conflict.practitionerName}</TableCell>
                                <TableCell>{conflict.organizationName}</TableCell>
                                <TableCell>
                                    <Text>{conflict.currentBankAccount?.number}</Text>
                                    <Text>{conflict.currentBankAccount?.subscriber}</Text>
                                </TableCell>
                                <TableCell>
                                    <Text>{conflict.importedBankAccount.number}</Text>
                                    <Text>{conflict.importedBankAccount.subscriber}</Text>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    function renderInfoMessage() {
        setTimeout(() => setInfoMessage(undefined), 10000);

        return (
            <Alert
                appearance="info"
                hasIcon={true}
            >
                {infoMessage}
            </Alert>
        );
    }
}
